import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE, fadeUp } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

export const Philosophy = ({ content }) => {
    const p = content?.philosophy || {};
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    // Two shared parallax tracks instead of four; tightened range for calmer motion + cheaper compositing.
    const yEven = useTransform(scrollYProgress, [0, 1], [24, -24]);
    const yOdd = useTransform(scrollYProgress, [0, 1], [-18, 28]);

    return (
        <section
            id="philosophy"
            ref={ref}
            className="relative py-10 md:py-24 bg-[#0A0A0A] text-white overflow-hidden"
            data-testid="philosophy-section"
        >
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    <div className="lg:col-span-7">
                        <motion.span
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="text-xs uppercase tracking-[0.24em] text-white/55 font-body"
                        >
                            {p.eyebrow}
                        </motion.span>

                        {/* Line-by-line reveal, opacity + y only (no blur filter) */}
                        <h2 className="mt-6 font-display font-semibold text-[48px] sm:text-[64px] md:text-[88px] lg:text-[104px] leading-[0.96] tracking-[-0.04em]">
                            {(p.quote_lines || []).map((line, idx) => (
                                <motion.span
                                    key={`q-${line || idx}`}
                                    initial={{ opacity: 0, y: 22 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.95, ease: EASE, delay: idx * 0.12 }}
                                    className="block"
                                >
                                    {line}
                                </motion.span>
                            ))}
                        </h2>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
                            className="mt-10 max-w-[540px] font-body text-lg md:text-xl text-white/68 leading-relaxed"
                        >
                            {p.paragraph}
                        </motion.p>

                        <motion.div {...fadeUp(0.75)} className="mt-6 flex flex-wrap gap-3">
                            {(p.highlight_phrases || []).map((h, i) => (
                                <span key={`hl-${h || i}`} className="font-editorial italic text-base md:text-lg" style={{ color: "#7FB3E0" }}>
                                    “{h}”
                                </span>
                            ))}
                        </motion.div>
                    </div>

                    <div className="lg:col-span-5 relative h-[560px] md:h-[680px]">
                        {(p.images || []).slice(0, 4).map((src, i) => {
                            const positions = [
                                { y: yEven, top: "0%", left: "0%", w: "70%", aspect: "aspect-[4/5]" },
                                { y: yOdd, top: "10%", left: "45%", w: "55%", aspect: "aspect-[3/4]" },
                                { y: yEven, top: "50%", left: "5%", w: "55%", aspect: "aspect-[5/4]" },
                                { y: yOdd, top: "60%", left: "50%", w: "50%", aspect: "aspect-[4/5]" },
                            ][i];
                            return (
                                <motion.div
                                    key={`phi-img-${i}-${src || ""}`}
                                    style={{
                                        y: positions.y,
                                        top: positions.top,
                                        left: positions.left,
                                        width: positions.w,
                                        willChange: "transform",
                                        transform: "translateZ(0)",
                                        backfaceVisibility: "hidden",
                                    }}
                                    className={`absolute ${positions.aspect}`}
                                    data-testid={`philosophy-image-${i}`}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.97 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.1 }}
                                        className="w-full h-full rounded-[28px] overflow-hidden"
                                        style={{ transform: "translateZ(0)" }}
                                    >
                                        <img
                                            src={resolveImg(src)}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                        />
                                    </motion.div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};
